import React, { useState, useEffect, useCallback } from 'react';
import { marketplaceApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Plus, Edit, Trash2, Building2, TrendingUp, Eye, EyeOff, 
  ExternalLink, MousePointer, Loader2, GripVertical
} from 'lucide-react';
import { toast } from 'sonner';

const CREDIT_CATEGORIES = [
  { id: 'credit_builder_loan', name: 'Credit Builder Loans' },
  { id: 'tradeline', name: 'Tradeline / Subscription Reporting' },
  { id: 'rent_reporting', name: 'Rent & Bill Reporting' },
  { id: 'subscription_reporting', name: 'Build Credit Without Debt' }
];

const FINANCIAL_CATEGORIES = [
  { id: 'beginner', name: 'Beginner Investing' },
  { id: 'automated', name: 'Automated Investing' },
  { id: 'retirement', name: 'Retirement Accounts' },
  { id: 'high_yield', name: 'High-Yield Savings' },
  { id: 'micro', name: 'Micro-Investing' },
  { id: 'family', name: 'Kids / Family Investing' }
];

const BUREAUS = ['Experian', 'Equifax', 'TransUnion'];
const IMPACT_TAGS = ['Payment history', 'Credit mix', 'Utilization', 'Account age'];
const STATUS_BADGES = ['Popular', 'New', 'No Credit Check', 'Reports Monthly'];
const BENEFIT_TAGS = ['Automated', 'No Minimum', 'Diversified', 'Retirement', 'Roundups'];
const RISK_LEVELS = ['Low', 'Moderate', 'Market Risk'];

export default function MarketplaceAdmin() {
  const [creditProviders, setCreditProviders] = useState([]);
  const [financialProviders, setFinancialProviders] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('credit'); // 'credit' or 'financial'
  const [editingProvider, setEditingProvider] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const emptyCredit = {
    name: '', logo_url: '', description: '', category: 'credit_builder_loan',
    reporting_type: 'Loan', bureaus: [], monthly_cost_min: '', monthly_cost_max: '',
    impact_tags: [], status_badges: [], cta_label: 'Learn More', cta_url: '',
    affiliate_param: '', is_active: false, is_visible: true, display_order: 0
  };
  
  const emptyFinancial = {
    name: '', logo_url: '', description: '', category: 'beginner',
    investment_type: 'Savings', minimum_start: '', risk_level: 'Moderate',
    benefit_tags: [], cta_label: 'Open Account', cta_url: '',
    affiliate_param: '', is_active: false, is_visible: true, display_order: 0
  };
  
  const [form, setForm] = useState(emptyCredit);

  useEffect(function() {
    fetchData();
  }, []);

  async function fetchData() {
    setLoading(true);
    try {
      const [credit, financial, stats] = await Promise.all([
        marketplaceApi.adminGetCreditProviders(),
        marketplaceApi.adminGetFinancialProviders(),
        marketplaceApi.getAnalytics()
      ]);
      setCreditProviders(credit.providers || []);
      setFinancialProviders(financial.providers || []);
      setAnalytics(stats);
    } catch (err) {
      console.error('Failed to fetch marketplace data:', err);
      toast.error('Failed to load marketplace data');
    } finally {
      setLoading(false);
    }
  }

  function openAddDialog(type) {
    setDialogType(type);
    setEditingProvider(null);
    setForm(type === 'credit' ? emptyCredit : emptyFinancial);
    setDialogOpen(true);
  }

  function openEditDialog(provider, type) {
    setDialogType(type);
    setEditingProvider(provider);
    setForm({
      ...provider,
      monthly_cost_min: provider.monthly_cost_min || '',
      monthly_cost_max: provider.monthly_cost_max || ''
    });
    setDialogOpen(true);
  }

  async function handleSave() {
    if (!form.name || !form.description) {
      toast.error('Name and description are required');
      return;
    }
    
    setSaving(true);
    try {
      const data = {
        ...form,
        monthly_cost_min: form.monthly_cost_min ? parseFloat(form.monthly_cost_min) : null,
        monthly_cost_max: form.monthly_cost_max ? parseFloat(form.monthly_cost_max) : null
      };
      
      if (dialogType === 'credit') {
        if (editingProvider) {
          await marketplaceApi.adminUpdateCreditProvider(editingProvider.provider_id, data);
          toast.success('Credit provider updated');
        } else {
          await marketplaceApi.adminCreateCreditProvider(data);
          toast.success('Credit provider created');
        }
      } else {
        if (editingProvider) {
          await marketplaceApi.adminUpdateFinancialProvider(editingProvider.provider_id, data);
          toast.success('Financial provider updated');
        } else {
          await marketplaceApi.adminCreateFinancialProvider(data);
          toast.success('Financial provider created');
        }
      }
      
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error('Failed to save provider');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(providerId, type) {
    if (!window.confirm('Are you sure you want to delete this provider?')) return;
    
    try {
      if (type === 'credit') {
        await marketplaceApi.adminDeleteCreditProvider(providerId);
      } else {
        await marketplaceApi.adminDeleteFinancialProvider(providerId);
      }
      toast.success('Provider deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete provider');
    }
  }

  function toggleArrayItem(array, item) {
    if (array.includes(item)) {
      return array.filter(function(i) { return i !== item; });
    }
    return [...array, item];
  }

  function renderProviderRow(provider, type) {
    return (
      <div key={provider.provider_id} className="flex items-center justify-between p-4 bg-white rounded-lg border border-slate-200 hover:shadow-sm transition-shadow">
        <div className="flex items-center gap-4">
          <GripVertical className="h-4 w-4 text-slate-300 cursor-grab" />
          <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center overflow-hidden">
            {provider.logo_url ? (
              <img src={provider.logo_url} alt="" className="w-full h-full object-contain" />
            ) : (
              type === 'credit' ? <Building2 className="h-5 w-5 text-slate-400" /> : <TrendingUp className="h-5 w-5 text-slate-400" />
            )}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-slate-800">{provider.name}</span>
              {provider.is_active ? (
                <Badge className="bg-green-100 text-green-700">Active</Badge>
              ) : (
                <Badge className="bg-amber-100 text-amber-700">Coming Soon</Badge>
              )}
              {!provider.is_visible && (
                <Badge variant="outline" className="text-slate-500"><EyeOff className="h-3 w-3 mr-1" />Hidden</Badge>
              )}
            </div>
            <p className="text-sm text-slate-500 line-clamp-1">{provider.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right text-sm">
            <div className="flex items-center gap-1 text-slate-500">
              <MousePointer className="h-3 w-3" />
              {provider.click_count || 0} clicks
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={function() { openEditDialog(provider, type); }}>
            <Edit className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" className="text-red-600" onClick={function() { handleDelete(provider.provider_id, type); }}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-slate-800">{creditProviders.length}</div>
            <p className="text-sm text-slate-500">Credit Providers</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-slate-800">{financialProviders.length}</div>
            <p className="text-sm text-slate-500">Financial Providers</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-teal-600">{analytics?.total_credit_clicks || 0}</div>
            <p className="text-sm text-slate-500">Credit Clicks</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-blue-600">{analytics?.total_financial_clicks || 0}</div>
            <p className="text-sm text-slate-500">Financial Clicks</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="credit">
        <TabsList>
          <TabsTrigger value="credit"><Building2 className="h-4 w-4 mr-2" />Credit Builder</TabsTrigger>
          <TabsTrigger value="financial"><TrendingUp className="h-4 w-4 mr-2" />Financial Growth</TabsTrigger>
        </TabsList>
        
        <TabsContent value="credit" className="mt-4 space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Credit Builder Providers</h3>
            <Button onClick={function() { openAddDialog('credit'); }} className="btn-teal">
              <Plus className="h-4 w-4 mr-2" />Add Provider
            </Button>
          </div>
          {creditProviders.length === 0 ? (
            <Card className="border-dashed"><CardContent className="py-8 text-center text-slate-500">No credit providers yet. Add your first one!</CardContent></Card>
          ) : (
            <div className="space-y-2">
              {creditProviders.map(function(p) { return renderProviderRow(p, 'credit'); })}
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="financial" className="mt-4 space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Financial Growth Providers</h3>
            <Button onClick={function() { openAddDialog('financial'); }} className="btn-teal">
              <Plus className="h-4 w-4 mr-2" />Add Provider
            </Button>
          </div>
          {financialProviders.length === 0 ? (
            <Card className="border-dashed"><CardContent className="py-8 text-center text-slate-500">No financial providers yet. Add your first one!</CardContent></Card>
          ) : (
            <div className="space-y-2">
              {financialProviders.map(function(p) { return renderProviderRow(p, 'financial'); })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingProvider ? 'Edit' : 'Add'} {dialogType === 'credit' ? 'Credit Builder' : 'Financial Growth'} Provider
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Provider Name *</Label>
                <Input value={form.name} onChange={function(e) { setForm(function(f) { return {...f, name: e.target.value}; }); }} placeholder="e.g., Self Credit Builder" />
              </div>
              <div className="space-y-2">
                <Label>Logo URL</Label>
                <Input value={form.logo_url} onChange={function(e) { setForm(function(f) { return {...f, logo_url: e.target.value}; }); }} placeholder="https://..." />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea value={form.description} onChange={function(e) { setForm(function(f) { return {...f, description: e.target.value}; }); }} placeholder="Brief description of the service..." rows={2} />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={form.category} onValueChange={function(v) { setForm(function(f) { return {...f, category: v}; }); }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(dialogType === 'credit' ? CREDIT_CATEGORIES : FINANCIAL_CATEGORIES).map(function(c) {
                      return <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{dialogType === 'credit' ? 'Reporting Type' : 'Investment Type'}</Label>
                <Input 
                  value={dialogType === 'credit' ? form.reporting_type : form.investment_type} 
                  onChange={function(e) { 
                    setForm(function(f) { 
                      return dialogType === 'credit' 
                        ? {...f, reporting_type: e.target.value} 
                        : {...f, investment_type: e.target.value}; 
                    }); 
                  }} 
                  placeholder={dialogType === 'credit' ? 'Loan, Tradeline, etc.' : 'Savings, Robo, etc.'} 
                />
              </div>
            </div>
            
            {dialogType === 'credit' && (
              <>
                <div className="space-y-2">
                  <Label>Bureaus Reported To</Label>
                  <div className="flex gap-2">
                    {BUREAUS.map(function(b) {
                      return (
                        <Button 
                          key={b} 
                          type="button" 
                          variant={form.bureaus.includes(b) ? 'default' : 'outline'} 
                          size="sm"
                          onClick={function() { setForm(function(f) { return {...f, bureaus: toggleArrayItem(f.bureaus, b)}; }); }}
                        >
                          {b}
                        </Button>
                      );
                    })}
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Monthly Cost Min ($)</Label>
                    <Input type="number" value={form.monthly_cost_min} onChange={function(e) { setForm(function(f) { return {...f, monthly_cost_min: e.target.value}; }); }} placeholder="0" />
                  </div>
                  <div className="space-y-2">
                    <Label>Monthly Cost Max ($)</Label>
                    <Input type="number" value={form.monthly_cost_max} onChange={function(e) { setForm(function(f) { return {...f, monthly_cost_max: e.target.value}; }); }} placeholder="25" />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Credit Impact Tags</Label>
                  <div className="flex flex-wrap gap-2">
                    {IMPACT_TAGS.map(function(t) {
                      return (
                        <Button 
                          key={t} 
                          type="button" 
                          variant={form.impact_tags.includes(t) ? 'default' : 'outline'} 
                          size="sm"
                          onClick={function() { setForm(function(f) { return {...f, impact_tags: toggleArrayItem(f.impact_tags, t)}; }); }}
                        >
                          {t}
                        </Button>
                      );
                    })}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Status Badges</Label>
                  <div className="flex flex-wrap gap-2">
                    {STATUS_BADGES.map(function(b) {
                      return (
                        <Button 
                          key={b} 
                          type="button" 
                          variant={form.status_badges.includes(b) ? 'default' : 'outline'} 
                          size="sm"
                          onClick={function() { setForm(function(f) { return {...f, status_badges: toggleArrayItem(f.status_badges, b)}; }); }}
                        >
                          {b}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
            
            {dialogType === 'financial' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Minimum to Start</Label>
                    <Input value={form.minimum_start} onChange={function(e) { setForm(function(f) { return {...f, minimum_start: e.target.value}; }); }} placeholder="$0, $100, etc." />
                  </div>
                  <div className="space-y-2">
                    <Label>Risk Level</Label>
                    <Select value={form.risk_level} onValueChange={function(v) { setForm(function(f) { return {...f, risk_level: v}; }); }}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {RISK_LEVELS.map(function(r) { return <SelectItem key={r} value={r}>{r}</SelectItem>; })}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Benefit Tags</Label>
                  <div className="flex flex-wrap gap-2">
                    {BENEFIT_TAGS.map(function(t) {
                      return (
                        <Button 
                          key={t} 
                          type="button" 
                          variant={form.benefit_tags.includes(t) ? 'default' : 'outline'} 
                          size="sm"
                          onClick={function() { setForm(function(f) { return {...f, benefit_tags: toggleArrayItem(f.benefit_tags, t)}; }); }}
                        >
                          {t}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
            
            <div className="border-t pt-4 space-y-4">
              <h4 className="font-medium text-slate-700">Affiliate Link Settings</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>CTA Button Label</Label>
                  <Input value={form.cta_label} onChange={function(e) { setForm(function(f) { return {...f, cta_label: e.target.value}; }); }} placeholder="Learn More" />
                </div>
                <div className="space-y-2">
                  <Label>Display Order</Label>
                  <Input type="number" value={form.display_order} onChange={function(e) { setForm(function(f) { return {...f, display_order: parseInt(e.target.value) || 0}; }); }} placeholder="0" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Affiliate URL</Label>
                <Input value={form.cta_url} onChange={function(e) { setForm(function(f) { return {...f, cta_url: e.target.value}; }); }} placeholder="https://partner.com/signup" />
                <p className="text-xs text-slate-500">Leave empty to show "Coming Soon"</p>
              </div>
              <div className="space-y-2">
                <Label>Affiliate Parameter</Label>
                <Input value={form.affiliate_param} onChange={function(e) { setForm(function(f) { return {...f, affiliate_param: e.target.value}; }); }} placeholder="ref=aicreditpilot" />
                <p className="text-xs text-slate-500">Will be appended to URL automatically</p>
              </div>
            </div>
            
            <div className="flex items-center justify-between border-t pt-4">
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Switch checked={form.is_active} onCheckedChange={function(c) { setForm(function(f) { return {...f, is_active: c}; }); }} />
                  <Label>Active (Link Ready)</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={form.is_visible} onCheckedChange={function(c) { setForm(function(f) { return {...f, is_visible: c}; }); }} />
                  <Label>Visible to Users</Label>
                </div>
              </div>
              <Button onClick={handleSave} disabled={saving} className="btn-teal">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {editingProvider ? 'Update' : 'Create'} Provider
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
